import React, { lazy, Suspense, useState, useEffect, useRef } from "react";
import VatToggle from "@/components/prices/VatToggle";
import { useVatMode } from "@/hooks/useVatMode";
import { Link, useNavigate } from "@/lib/router-compat";
import { 
  Search, 
  TrendingUp, 
  Lock, 
  Clock, 
  BarChart3, 
  Building,
  ChevronDown, 
  Menu, 
  X,
  Zap,
  Calculator,
  Home,
  FileText,
  Building2,
  Sun,
  Activity,
  TrendingDown,
  Calendar,
  BookOpen,
  Lightbulb,
  Settings,
  MapPin,
  User,
  LogIn,
  ShieldCheck,
  Smartphone,
  Code,
  ClipboardList,
  BatteryCharging,
  BatteryFull,
  CarFront,
  Factory,
  Flame,
  PlugZap,
  EvCharger,
  Mail,
  Timer,
  Sparkles,
  Scale,

} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { usePartnerAccess } from "@/hooks/usePartnerAccess";
// Sökrutan (med ordlista och kommunlista) laddas först när sökningen öppnas,
// så att den inte ligger i huvudpaketet på varje sida.
const SiteSearchBox = lazy(() => import("@/components/layout/SiteSearchBox"));
import HeaderAskAi from "@/components/layout/HeaderAskAi";
import { TOOLS } from "@/config/toolsRegistry";
import { useToolsConfig } from "@/hooks/useToolsConfig";
import { useMainNavigationVersion } from "@/hooks/useMainNavigationVersion";
import navLogo from "@/assets/elmarknad-logo-nav.webp";

const Navbar = () => {
  const { vatMode, setVatMode } = useVatMode();
  const { isToolEnabled } = useToolsConfig();
  const { version: mainNavigationVersion, loading: mainNavigationLoading } = useMainNavigationVersion();
  const [showElpriserDropdown, setShowElpriserDropdown] = useState(false);
  const [showElavtalDropdown, setShowElavtalDropdown] = useState(false);
  const [showElbolagDropdown, setShowElbolagDropdown] = useState(false);
  const [showSpotprisDropdown, setShowSpotprisDropdown] = useState(false);
  const [showVerktygDropdown, setShowVerktygDropdown] = useState(false);
  const [showOffertDropdown, setShowOffertDropdown] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);

  const [showSiteSearch, setShowSiteSearch] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isAdmin } = useAdmin();
  const { isPartner } = usePartnerAccess();
  const navigate = useNavigate();

  useEffect(() => {
    // Check initial auth state
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowSiteSearch((prev) => !prev);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const [showSecondaryHeader, setShowSecondaryHeader] = useState(true);
  const showSecondaryHeaderRef = useRef(showSecondaryHeader);
  useEffect(() => {
    showSecondaryHeaderRef.current = showSecondaryHeader;
  }, [showSecondaryHeader]);

  // Scroll-driven secondary header visibility
  // Throttled to one rAF per frame, with debounced state updates and large
  // jump suppression to avoid jank, flapping, and unnecessary CPU usage.
  const lastScrollY = useRef(0);
  const anchorScrollY = useRef(0);
  const rafId = useRef<number | null>(null);
  const lastTick = useRef(0);
  const pendingShow = useRef<boolean | null>(null);
  const SCROLL_THROTTLE_MS = 16; // ~60 fps
  const SCROLL_TOLERANCE = 40;
  const JUMP_THRESHOLD = 300;

  useEffect(() => {
    if (typeof window === "undefined") return;

    lastScrollY.current = window.scrollY;
    anchorScrollY.current = window.scrollY;

    const applyPending = () => {
      if (pendingShow.current !== null && pendingShow.current !== showSecondaryHeaderRef.current) {
        setShowSecondaryHeader(pendingShow.current);
      }
      pendingShow.current = null;
    };

    const handleScroll = () => {
      const now = performance.now();
      if (now - lastTick.current < SCROLL_THROTTLE_MS) return;
      if (rafId.current !== null) return;

      rafId.current = window.requestAnimationFrame((frameTime) => {
        rafId.current = null;
        lastTick.current = frameTime;

        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollY.current;

        // Ignore large programmatic jumps (scroll restoration, anchor jumps,
        // lazy content layout shifts) so the header doesn't flap.
        if (Math.abs(delta) > JUMP_THRESHOLD) {
          lastScrollY.current = currentScrollY;
          anchorScrollY.current = currentScrollY;
          return;
        }

        if (currentScrollY <= 80) {
          pendingShow.current = true;
          anchorScrollY.current = currentScrollY;
        } else if (showSecondaryHeaderRef.current) {
          if (delta > 0 && currentScrollY > anchorScrollY.current + SCROLL_TOLERANCE) {
            pendingShow.current = false;
            anchorScrollY.current = currentScrollY;
          } else if (delta < 0) {
            anchorScrollY.current = currentScrollY;
          }
        } else {
          if (currentScrollY > anchorScrollY.current) {
            anchorScrollY.current = currentScrollY;
          }
          if (delta < 0 && currentScrollY < anchorScrollY.current - SCROLL_TOLERANCE) {
            pendingShow.current = true;
            anchorScrollY.current = currentScrollY;
          }
        }

        lastScrollY.current = currentScrollY;
        applyPending();
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId.current !== null) {
        window.cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, []);



  const elpriserMenuItems = [
    {
      icon: Search,
      title: "Jämför elpriser",
      description: "Sök, filtrera & byt till det bästa elavtalet",
      link: "/elpriser/jamfor"
    },
    {
      icon: Zap,
      title: "Elpriser just nu",
      description: "Aktuella priser och jämför elavtal",
      link: "/elpriser"
    },
    {
      icon: TrendingUp,
      title: "Månadspris",
      description: "Vad är månadspris och vilket elbolag har bäst månadspris priser?",
      link: "/elavtal/manadspris"
    },
    {
      icon: Lock,
      title: "Fast elpris",
      description: "Vad är fast elpris och vilket elbolag har bäst fasta priser?",
      link: "/elavtal/fast"
    },
    {
      icon: Clock,
      title: "Timpris",
      description: "Vad är timpris och vilket elbolag har bäst timprisavtal?",
      link: "/elavtal/timpris"
    },
    {
      icon: Timer,
      title: "Kvartspris",
      description: "Spotpriset per 15 minuter – så fungerar kvartspriser",
      link: "/elavtal/kvartspris"
    }

  ];

  const elomradeMenuItems = [
    { title: "Elområde 1 (SE1) – Luleå", link: "/elpriser/elomrade-1" },
    { title: "Elområde 2 (SE2) – Sundsvall", link: "/elpriser/elomrade-2" },
    { title: "Elområde 3 (SE3) – Stockholm", link: "/elpriser/elomrade-3" },
    { title: "Elområde 4 (SE4) – Malmö", link: "/elpriser/elomrade-4" },
  ];

  const elavtalMenuItems = [
    {
      icon: Search,
      title: "Jämför elavtal",
      description: "Hitta och jämför de bästa elavtalen",
      link: "/elavtal/jamfor"
    },
    {
      icon: Lightbulb,
      title: "Avtalsväljaren",
      description: "Få personliga rekommendationer på det bästa avtalet för dig",
      link: "/elavtal/avtalsvaljaren"
    },
    {
      icon: FileText,
      title: "Teckna elavtal",
      description: "Steg-för-steg guide för att teckna avtal",
      link: "/elavtal/teckna"
    },
    {
      icon: TrendingDown,
      title: "Byt elavtal",
      description: "Guide för att enkelt byta elavtal",
      link: "/elavtal/byt"
    },
    {
      icon: TrendingUp,
      title: "Månadsprisavtal",
      description: "Följer marknadens spotpris månad för månad",
      link: "/elavtal/manadspris"
    },
    {
      icon: Clock,
      title: "Timprisavtal",
      description: "Betalar spotpriset timme för timme",
      link: "/elavtal/timpris"
    },
    {
      icon: Timer,
      title: "Kvartsprisavtal",
      description: "Spotpriset per 15 minuter – för dig som styr förbrukningen",
      link: "/elavtal/kvartspris"
    },
    {
      icon: Lock,
      title: "Fast elavtal",
      description: "Fast pris under hela avtalsperioden",
      link: "/elavtal/fast"
    },
    {
      icon: Activity,
      title: "Rörligt pris",
      description: "Samlingsnamnet för månads-, tim- och kvartsprisavtal",
      link: "/elavtal/rorligt"
    }

  ].filter((item) => {
    const tool = TOOLS.find((t) => t.path === item.link);
    return !tool || isToolEnabled(tool.id);
  });


  const elbolagMenuItems = [
    {
      icon: Scale,
      title: "Jämför elbolag",
      description: "Jämför elavtal och priser",
      link: "/elbolag/jamfor"
    },
    {
      icon: Building2,
      title: "Elhandelsbolag: Lista",
      description: "Alla elhandelsbolag i Sverige A–Ö",
      link: "/elhandel"
    },
    {
      icon: Zap,
      title: "Elnätsbolag: Lista",
      description: "Alla elnätsbolag i Sverige A–Ö",
      link: "/elnat"
    },

    {
      icon: TrendingUp,
      title: "Bästa elbolaget",
      description: "Jämförelse av Sveriges bästa elbolag",
      link: "/elbolag/basta"
    },
    {
      icon: TrendingDown,
      title: "Billigaste elbolaget",
      description: "Hitta det billigaste elbolaget",
      link: "/elbolag/billigaste"
    },
    {
      icon: TrendingDown,
      title: "Byt elbolag",
      description: "Guide för att byta elbolag",
      link: "/elbolag/byt"
    }
  ];

  const elbolagVerktyg = [
    { title: "Vilket elbolag har jag?", link: "/verktyg/hitta-elbolag" },
    { title: "Hitta nätägare", link: "/verktyg/hitta-natagare" },
  ].filter((item) => {
    const tool = TOOLS.find((t) => t.path === item.link);
    return !tool || isToolEnabled(tool.id);
  });

  const spotprisPages = [
    { icon: Activity, title: "Spotpris idag", link: "/spotpris/idag" },
    { icon: Calendar, title: "Spotpris imorgon", link: "/spotpris/imorgon" },
    { icon: TrendingDown, title: "Historiska priser", link: "/elpriser/historik" },
    { icon: BarChart3, title: "Elprisprognos", link: "/elpriser/prognos" },
  ];


  const verktyg = [
    { icon: Sparkles, title: "Elmarknad AI", link: "/ai" },
    { icon: Lightbulb, title: "Avtalsväljaren", link: "/elavtal/avtalsvaljaren" },
    { icon: FileText, title: "Mitt elpris", link: "/mitt-elpris" },
    { icon: Calculator, title: "AI-analys av elfaktura", link: "/ai-fakturaanalys" },
    { icon: Calculator, title: "AI-analys av energibehov", link: "/personlig-energianalys" },
    { icon: MapPin, title: "Hitta nätägare", link: "/verktyg/hitta-natagare" },
    { icon: MapPin, title: "Hitta elområde", link: "/verktyg/hitta-elomrade" },
    { icon: FileText, title: "Mina sidor", link: "/mina-sidor" },
    { icon: Smartphone, title: "Mobilapp", link: "/verktyg/mobilapp" },
    { icon: Code, title: "Elpris-API", link: "/elpris-api" },
    { icon: BarChart3, title: "Elprisprognos API", link: "/prognos-api" },
  ].filter((item) => {
    const tool = TOOLS.find((t) => t.path === item.link);
    return !tool || isToolEnabled(tool.id);
  });

  const spotprisAreas = [
    { title: "SE1 (Luleå)", link: "/spotpris/se1" },
    { title: "SE2 (Sundsvall)", link: "/spotpris/se2" },
    { title: "SE3 (Stockholm)", link: "/spotpris/se3" },
    { title: "SE4 (Malmö)", link: "/spotpris/se4" },
  ];

  const offertItems = [
    { icon: Sun, title: "Solceller", link: "/solceller" },
    { icon: BatteryCharging, title: "Hembatterier", link: "/batterier" },
    { icon: Flame, title: "Värmepumpar", link: "/varmepumpar" },
    { icon: PlugZap, title: "Laddboxar", link: "/laddboxar" },
    { icon: CarFront, title: "Laddstolpar", link: "/laddstolpar" },
    { icon: Factory, title: "Kommersiella solceller", link: "/kommersiella-solceller" },
    { icon: BatteryFull, title: "Kommersiella batterier", link: "/kommersiella-batterier" },
  ];

  const navigationV2MainItems = [
    { title: "Elavtal", link: "/elavtal/jamfor", icon: FileText },
    { title: "Solceller", link: "/solceller", icon: Sun },
    { title: "Batterier", link: "/batterier", icon: BatteryCharging },
    { title: "Spotpris", link: "/spotpris", icon: Activity },
  ];

  const navigationV2PopularItems = [
    { title: "Elpriser just nu", link: "/elpriser/idag", icon: Zap },
    { title: "Spotpris idag", link: "/spotpris/idag", icon: Activity },
    { title: "Spotpris imorgon", link: "/spotpris/imorgon", icon: Calendar },
    { title: "Historiska elpriser", link: "/elpriser/historik", icon: TrendingDown },
    { title: "Elprisprognos", link: "/elpriser/prognos", icon: BarChart3 },
    { title: "Mobilapp", link: "/verktyg/mobilapp", icon: Smartphone },
    { title: "Hitta nätägare", link: "/verktyg/hitta-natagare", icon: MapPin },
    { title: "Hitta laddstation", link: "/laddstationer", icon: EvCharger },
    { title: "Elavtal för företag", link: "/elavtal/foretag", icon: Building2 },
    { title: "Elavtal för BRF", link: "/elavtal/brf", icon: Building },
  ];




  const mobileMenuSections = [
    {
      label: "Elpriser",
      icon: Zap,
      mainPage: "/elpriser",
      items: [
        ...elpriserMenuItems,
        { icon: Activity, title: "Dagens elpriser", link: "/elpriser/idag" },
        { icon: Calendar, title: "Morgondagens elpriser", link: "/elpriser/imorgon" },
        { icon: TrendingDown, title: "Historiska priser", link: "/elpriser/historik" },
        { icon: BarChart3, title: "Elprisprognos", link: "/elpriser/prognos" },
        ...elomradeMenuItems.map((o) => ({ icon: MapPin, title: o.title, link: o.link })),
      ]
    },
    {
      label: "Elavtal",
      icon: FileText,
      mainPage: "/elavtal",
      items: [
        ...elavtalMenuItems,
        { icon: Home, title: "Elavtal för villa", link: "/elavtal/villa" },
        { icon: Building2, title: "Elavtal för lägenhet", link: "/elavtal/lagenhet" },
        { icon: User, title: "Elavtal för studenter", link: "/elavtal/student" },
        { icon: User, title: "Elavtal för pensionärer", link: "/elavtal/pensionar" },
        { icon: Building2, title: "Elavtal för företag", link: "/elavtal/foretag" },
        { icon: Building2, title: "Elavtal för BRF", link: "/elavtal/brf" },
      ]
    },
    {
      label: "Elbolag",
      icon: Building2,
      mainPage: "/elbolag",
      items: [
        ...elbolagMenuItems,
      ]
    },
    {
      label: "Spotpriser",
      icon: Activity,
      mainPage: "/spotpris",
      items: [
        ...spotprisPages,
        { icon: MapPin, title: "SE1 (Luleå)", link: "/spotpris/se1" },
        { icon: MapPin, title: "SE2 (Sundsvall)", link: "/spotpris/se2" },
        { icon: MapPin, title: "SE3 (Stockholm)", link: "/spotpris/se3" },
        { icon: MapPin, title: "SE4 (Malmö)", link: "/spotpris/se4" },
      ]
    },
    {
      label: "Offertjämförelse",
      icon: ClipboardList,
      mainPage: "/offerter",
      items: offertItems
    },
    {
      label: "Verktyg",
      icon: Settings,
      mainPage: "/verktyg",
      items: verktyg

    }
  ];

  return (
    <>
      {/* Admin banner - tablet & mobile only */}
      {(isAdmin || isPartner) && (
        <div className="xl:hidden bg-primary text-primary-foreground">
          <div className="page-container py-2 flex items-center justify-center gap-4 text-sm">
            {isAdmin && (
              <Link
                to="/admin"
                className="font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <ShieldCheck size={16} />
                Admin
              </Link>
            )}
            {isPartner && (
              <Link
                to="/partner"
                className="font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <ShieldCheck size={16} />
                Partnerportal
              </Link>
            )}
          </div>
        </div>
      )}


      <header className="sticky top-0 z-50">
        {/* Secondary Header - Desktop Only */}
        <div
          data-testid="secondary-header"
          className={`hidden xl:block overflow-hidden bg-card will-change-transform transition-[height,opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            showSecondaryHeader ? 'h-12 opacity-100 translate-y-0' : 'h-0 opacity-0 -translate-y-2'
          }`}
        >
          <div className="page-container h-full py-2 grid grid-cols-3 items-center text-sm border-b">
            <div className="flex items-center gap-4 text-muted-foreground">
              <a href="mailto:info@elmarknad.se" className="hover:text-primary transition-colors flex min-h-6 items-center gap-2">
                <Mail size={16} />
                <span>info@elmarknad.se</span>
              </a>
            </div>
            <div className="flex justify-center items-center gap-4">
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-foreground font-medium hover:text-primary transition-colors flex items-center gap-2"
                >
                  <ShieldCheck size={16} />
                  Admin
                </Link>
              )}
              {isPartner && (
                <Link
                  to="/partner"
                  className="text-foreground font-medium hover:text-primary transition-colors flex items-center gap-2"
                >
                  <ShieldCheck size={16} />
                  Partnerportal
                </Link>
              )}
            </div>
            <div className="flex justify-end items-center gap-4">
              <Link
                to={isAuthenticated ? "/mina-sidor" : "/logga-in"}
                className="inline-flex min-h-6 items-center text-foreground font-medium hover:text-primary transition-colors"
              >
                {isAuthenticated ? "Mina sidor" : "Logga in"}
              </Link>
              <VatToggle value={vatMode} onChange={setVatMode} size="sm" />
            </div>
          </div>
        </div>


        {/* Main Navigation */}
        <nav className="relative z-50 w-full bg-card shadow-xs">
      <div className="page-container">
        <div className="flex justify-between items-center py-3">
          <Link to="/" className="shrink-0 min-w-0">
            <img
              src={navLogo}
              alt="Elmarknad"
              width={480}
              height={124}
              fetchPriority="high"
              decoding="async"
              className="h-8 sm:h-10 md:h-[48px] w-auto max-w-[120px] sm:max-w-[180px] md:max-w-none object-contain"
            />
          </Link>


          
          {/* Desktop inline search (replaces center navigation) */}
          {showSiteSearch && (
            <div className="hidden xl:block flex-1 mx-6">
              <Suspense fallback={<div className="h-11 rounded-full bg-muted" aria-hidden="true" />}>
                <SiteSearchBox onClose={() => setShowSiteSearch(false)} />
              </Suspense>
            </div>
          )}

          {/* Desktop Navigation Links */}
          {mainNavigationLoading ? null : mainNavigationVersion === "v1" ? (
          <div className={`${showSiteSearch ? "hidden" : "hidden xl:flex"} items-center gap-6`}>

            <div 
              className="relative group"
              onMouseEnter={() => setShowElpriserDropdown(true)}
              onMouseLeave={() => setShowElpriserDropdown(false)}
            >
              <Link 
                to="/elpriser"
                className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1"
              >
                Elpriser
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showElpriserDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[600px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-6">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - Menu Items */}
                      <div className="space-y-3">
                        {elpriserMenuItems.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.link}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                          >
                            <div className="mt-0.5 text-primary">
                              <item.icon size={20} />
                            </div>
                            <div>
                              <div className="font-semibold text-foreground group-hover/item:text-primary transition-colors">
                                {item.title}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {item.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      
                      {/* Right Column - Categories */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="heading-3 mb-3">Populära sidor</h3>
                          <div className="space-y-2">
                            <Link to="/elpriser/idag" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Dagens elpriser
                            </Link>
                            <Link to="/elpriser/imorgon" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Morgondagens elpriser
                            </Link>
                            <Link to="/elpriser/historik" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Historiska priser
                            </Link>
                            <Link to="/elpriser/prognos" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elprisprognos
                            </Link>
                          </div>
                        </div>
                        <div>
                          <h3 className="heading-3 mb-3">Elområden</h3>
                          <div className="space-y-2">
                            {elomradeMenuItems.map((o) => (
                              <Link
                                key={o.link}
                                to={o.link}
                                className="block py-1.5 text-muted-foreground hover:text-primary transition-colors"
                              >
                                {o.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div 
              className="relative group"
              onMouseEnter={() => setShowElavtalDropdown(true)}
              onMouseLeave={() => setShowElavtalDropdown(false)}
            >
              <Link 
                to="/elavtal"
                className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1"
              >
                Elavtal
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showElavtalDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[600px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-6">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - Menu Items */}
                      <div className="space-y-3">
                        {elavtalMenuItems.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.link}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                          >
                            <div className="mt-0.5 text-primary">
                              <item.icon size={20} />
                            </div>
                            <div>
                              <div className="font-semibold text-foreground group-hover/item:text-primary transition-colors">
                                {item.title}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {item.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      
                      {/* Right Column - Target Groups */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="heading-3 mb-3">För olika behov</h3>
                          <div className="space-y-2">
                            <Link to="/elavtal/villa" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för villa
                            </Link>
                            <Link to="/elavtal/lagenhet" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för lägenhet
                            </Link>
                            <Link to="/elavtal/student" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för studenter
                            </Link>
                            <Link to="/elavtal/pensionar" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för pensionärer
                            </Link>
                            <Link to="/elavtal/foretag" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för företag
                            </Link>
                            <Link to="/elavtal/brf" className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                              Elavtal för BRF
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setShowElbolagDropdown(true)}
              onMouseLeave={() => setShowElbolagDropdown(false)}
            >
              <Link 
                to="/elbolag"
                className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1"
              >
                Elbolag
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showElbolagDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[600px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-6">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - Menu Items */}
                      <div className="space-y-3">
                        {elbolagMenuItems.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.link}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                          >
                            <div className="mt-0.5 text-primary">
                              <item.icon size={20} />
                            </div>
                            <div>
                              <div className="font-semibold text-foreground group-hover/item:text-primary transition-colors">
                                {item.title}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {item.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      
                      {/* Right Column - Info */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="heading-3 mb-3">Verktyg</h3>
                          <div className="space-y-2">
                            {elbolagVerktyg.map((item) => (
                              <Link key={item.link} to={item.link} className="block py-1.5 text-muted-foreground hover:text-primary transition-colors">
                                {item.title}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setShowSpotprisDropdown(true)}
              onMouseLeave={() => setShowSpotprisDropdown(false)}
            >
              <Link to="/spotpris" className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1">
                Spotpris
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showSpotprisDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[550px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-6">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - Main Pages */}
                      <div className="space-y-2">
                        {spotprisPages.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.link}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                          >
                            <item.icon size={18} className="text-primary" />
                            <span className="text-foreground group-hover/item:text-primary transition-colors">
                              {item.title}
                            </span>
                          </Link>
                        ))}
                      </div>
                      
                      {/* Right Column - Areas */}
                      <div className="space-y-2">
                        <h3 className="heading-3 mb-3">Elområden</h3>
                        {spotprisAreas.map((area, idx) => (
                          <Link
                            key={idx}
                            to={area.link}
                            className="block py-2 px-3 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                          >
                            {area.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setShowOffertDropdown(true)}
              onMouseLeave={() => setShowOffertDropdown(false)}
            >
              <Link to="/offerter" className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1">
                Offertjämförelse
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showOffertDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[350px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-4">
                    <div className="space-y-2">
                      {offertItems.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.link}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                        >
                          <item.icon size={18} className="text-primary" />
                          <span className="text-foreground group-hover/item:text-primary transition-colors">
                            {item.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setShowVerktygDropdown(true)}
              onMouseLeave={() => setShowVerktygDropdown(false)}
            >
              <Link to="/verktyg" className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1">

                Verktyg
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </Link>
              
              {/* Dropdown Menu */}
              {showVerktygDropdown && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[350px] z-50">
                  <div className="bg-card rounded-xl shadow-2xl p-4">
                    <div className="space-y-2">
                      {verktyg.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.link}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group/item"
                        >
                          <item.icon size={18} className="text-primary" />
                          <span className="text-foreground group-hover/item:text-primary transition-colors">
                            {item.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          ) : (
            <div className={`${showSiteSearch ? "hidden" : "hidden xl:flex"} items-center gap-7`}>
              {navigationV2MainItems.map((item) => (
                <Link
                  key={item.link}
                  to={item.link}
                  className="flex items-center gap-2 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1"
                >
                  <item.icon size={18} className="shrink-0" />
                  {item.title}
                </Link>
              ))}
              <div
                className="relative"
                onMouseEnter={() => setShowMoreDropdown(true)}
                onMouseLeave={() => setShowMoreDropdown(false)}
              >
                <button
                  type="button"
                  className="flex items-center gap-1 text-foreground font-medium hover:text-primary transition-colors border-b-2 border-transparent hover:border-primary pb-1"
                  aria-expanded={showMoreDropdown}
                  aria-haspopup="menu"
                >
                  Mer
                  <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                </button>
                {showMoreDropdown && (
                  <div className="absolute right-0 top-full pt-2 w-[360px] z-50">
                    <div className="bg-card rounded-lg shadow-2xl p-4" role="menu">
                      <p className="px-3 pb-2 text-xs font-semibold uppercase text-muted-foreground">
                        Populära sidor
                      </p>
                      <div className="grid grid-cols-2 gap-1">
                        {navigationV2PopularItems.map((item) => (
                          <Link
                            key={item.link}
                            to={item.link}
                            role="menuitem"
                            className="flex items-center gap-2 rounded-md p-3 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                          >
                            <item.icon size={17} className="shrink-0 text-primary" />
                            <span>{item.title}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center gap-2">
            <HeaderAskAi />
            <Button
              variant="ghost"
              size="icon"
              aria-label={showSiteSearch ? "Stäng sök" : "Sök på hela sajten"}
              onClick={() => setShowSiteSearch((prev) => !prev)}
            >
              <Search size={20} />
            </Button>
            <Button onClick={() => navigate("/elavtal/jamfor")} variant="cta">
              Jämför elavtal
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="xl:hidden flex min-w-0 shrink items-center justify-end gap-1 sm:gap-2">
            <HeaderAskAi />

            <Button
              variant="ghost"
              size="icon"
              className="hidden h-9 w-9 shrink-0 min-[380px]:inline-flex"
              aria-label="Sök på hela sajten"
              onClick={() => setShowSiteSearch((prev) => !prev)}
            >
              <Search size={20} />
            </Button>

            <Button
              onClick={() => navigate("/elavtal/jamfor")}
              size="sm"
              variant="cta"
              className="h-9 shrink-0 px-2.5 text-xs sm:px-4 sm:text-sm"
            >
              <span className="hidden min-[380px]:inline">Jämför elavtal</span>
              <span className="min-[380px]:hidden">Jämför</span>
            </Button>


            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="Öppna meny">
                  <Menu size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto bg-card">
                <div className="py-6">
                  {/* Auth Section */}
                  <div className="mb-6 pb-6 border-b">
                    <SheetClose asChild>
                      <Link
                        to={isAuthenticated ? "/mina-sidor" : "/logga-in"}
                        className="text-foreground font-medium hover:text-primary transition-colors flex items-center gap-2"
                      >
                        {isAuthenticated ? (
                          <>
                            <User size={18} />
                            <span>Mina sidor</span>
                          </>
                        ) : (
                          <span>Logga in</span>
                        )}
                      </Link>
                    </SheetClose>
                  </div>

                  {mainNavigationLoading ? null : mainNavigationVersion === "v1" ? (
                  <>
                  {/* Collapsible Menu Sections */}
                  <Accordion type="multiple" className="w-full mb-6">
                    {mobileMenuSections.map((section, sectionIdx) => (
                      <AccordionItem key={sectionIdx} value={`section-${sectionIdx}`}>
                        <AccordionTrigger className="text-base font-medium hover:no-underline py-3">
                          <div className="flex items-center gap-2">
                            <section.icon size={18} className="text-primary" />
                            <span>{section.label}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-1 pt-2">
                            {/* Main Page Link */}
                            {section.mainPage && (
                              <SheetClose asChild>
                                <Link
                                  to={section.mainPage}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors font-semibold text-primary"
                                >
                                  <Home size={18} />
                                  <span>Visa huvudsida</span>
                                </Link>
                              </SheetClose>
                            )}
                            
                            {/* Section Items */}
                            {section.items.map((item, idx) => (
                              <SheetClose asChild key={idx}>
                                <Link
                                  to={item.link}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <item.icon size={18} className="text-primary flex-shrink-0" />
                                  <span className="text-foreground text-sm">{item.title}</span>
                                </Link>
                              </SheetClose>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>

                  {/* Direct Links - Nyheter & Rapporter at bottom */}
                  <div className="space-y-1 pt-6 border-t">
                    <SheetClose asChild>
                      <Link
                        to="/nyheter"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
                      >
                        <BookOpen size={18} className="text-primary" />
                        <span className="text-foreground font-medium">Nyheter</span>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link
                        to="/rapporter"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
                      >
                        <FileText size={18} className="text-primary" />
                        <span className="text-foreground font-medium">Rapporter</span>
                      </Link>
                    </SheetClose>
                  </div>
                  </>
                  ) : (
                    <div className="space-y-1 mb-6">
                      {navigationV2MainItems.map((item) => (
                        <SheetClose asChild key={item.link}>
                          <Link
                            to={item.link}
                            className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-muted transition-colors"
                          >
                            <item.icon size={19} className="shrink-0 text-primary" />
                            <span className="font-medium text-foreground">{item.title}</span>
                          </Link>
                        </SheetClose>
                      ))}
                      <div className="pt-5 mt-4 border-t">
                        <p className="px-3 pb-2 text-xs font-semibold uppercase text-muted-foreground">Mer</p>
                        {navigationV2PopularItems.map((item) => (
                          <SheetClose asChild key={item.link}>
                            <Link
                              to={item.link}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted transition-colors"
                            >
                              <item.icon size={18} className="shrink-0 text-primary" />
                              <span className="text-sm text-foreground">{item.title}</span>
                            </Link>
                          </SheetClose>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* VAT toggle at bottom of mobile menu */}
                  <div className="pt-6 border-t mt-6">
                    <div className="flex items-center justify-between px-3">
                      <span className="text-sm font-medium text-foreground">Visa priser</span>
                      <VatToggle value={vatMode} onChange={setVatMode} size="sm" />
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      {/* Mobile / tablet search bar below header */}
      {showSiteSearch && (
        <div className="xl:hidden border-t bg-card">
          <div className="page-container py-3">
            <Suspense fallback={<div className="h-11 rounded-full bg-muted" aria-hidden="true" />}>
              <SiteSearchBox variant="sheet" onClose={() => setShowSiteSearch(false)} />
            </Suspense>
          </div>
        </div>
      )}
    </nav>
    </header>
    </>


  );
};

export default Navbar;
